import { Injectable } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PropertyGroup } from './property-grouper.service';
import { UIStateService } from '../services/ui-state.service';

export interface DraggablePropertyGroup extends PropertyGroup {
  order: number;
  isDragging: boolean;
  customOrder?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DragDropPropertyManagerService {
  private customGroupOrders: Record<string, DraggablePropertyGroup[]> = {};

  constructor(private uiState: UIStateService) {}

  // Converte grupos de propriedades regulares em arrastáveis
  makeDraggable(groups: PropertyGroup[], componentType: string): DraggablePropertyGroup[] {
    const customOrder = this.getCustomOrder(componentType);
    
    return groups.map((group, index) => ({
      ...group,
      order: customOrder.findIndex(id => id === group.id) !== -1 
        ? customOrder.findIndex(id => id === group.id) 
        : index,
      isDragging: false,
      customOrder: customOrder.length > 0 ? customOrder : undefined,
    })).sort((a, b) => a.order - b.order);
  }

  // Gerencia reordenação de grupos de propriedades
  onPropertyGroupDrop(
    event: CdkDragDrop<DraggablePropertyGroup[]>, 
    componentType: string
  ): DraggablePropertyGroup[] {
    const groups = [...event.container.data];
    
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(groups, event.previousIndex, event.currentIndex);
      
      // Atualiza índices de ordem
      const reorderedGroups = groups.map((group, index) => ({
        ...group,
        order: index,
        isDragging: false,
      }));
      
      // Salva ordem personalizada
      this.saveCustomOrder(componentType, reorderedGroups.map(g => g.id));
      
      return reorderedGroups;
    }
    
    return groups.map(group => ({ ...group, isDragging: false }));
  }

  // Gerencia reordenação de propriedades dentro de um grupo
  onPropertyDrop(
    event: CdkDragDrop<string[]>, 
    groupId: string, 
    componentType: string
  ): string[] {
    const properties = [...event.container.data];
    
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(properties, event.previousIndex, event.currentIndex);
      
      // Salva ordem personalizada de propriedades para este grupo
      this.saveCustomPropertyOrder(componentType, groupId, properties);
    }
    
    return properties;
  }

  // Marca grupo como sendo arrastado
  setGroupDragging(groupId: string, isDragging: boolean, groups: DraggablePropertyGroup[]): DraggablePropertyGroup[] {
    return groups.map(group => ({
      ...group,
      isDragging: group.id === groupId ? isDragging : false,
    }));
  }

  // Obtém ordem personalizada de grupo para tipo de componente
  private getCustomOrder(componentType: string): string[] {
    const saved = localStorage.getItem(`property-group-order-${componentType}`);
    return saved ? JSON.parse(saved) : [];
  }

  // Salva ordem personalizada de grupo
  private saveCustomOrder(componentType: string, groupIds: string[]): void {
    localStorage.setItem(`property-group-order-${componentType}`, JSON.stringify(groupIds));
  }

  // Obtém ordem personalizada de propriedades dentro de um grupo
  getCustomPropertyOrder(componentType: string, groupId: string): string[] {
    const saved = localStorage.getItem(`property-order-${componentType}-${groupId}`);
    return saved ? JSON.parse(saved) : [];
  }

  // Salva ordem personalizada de propriedades dentro de um grupo
  private saveCustomPropertyOrder(componentType: string, groupId: string, properties: string[]): void {
    localStorage.setItem(`property-order-${componentType}-${groupId}`, JSON.stringify(properties));
  }

  // Reseta todas as ordens personalizadas para um tipo de componente
  resetCustomOrders(componentType: string): void {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(`property-group-order-${componentType}`) ||
      key.startsWith(`property-order-${componentType}-`)
    );
    
    keys.forEach(key => localStorage.removeItem(key));
  }

  // Obtém texto de prévia do arrasto
  getDragPreviewText(item: any, type: 'group' | 'property'): string {
    if (type === 'group') {
      return `📁 ${item.title} (${item.properties.length} properties)`;
    } else {
      return `⚙️ ${item}`;
    }
  }

  // Verifica se ordenação personalizada está habilitada
  isCustomOrderingEnabled(): boolean {
    return this.uiState.getCurrentState().inspector.activePropertyGroup !== 'search';
  }

  // Obtém visibilidade da alça de arrasto
  shouldShowDragHandle(screenSize: string): boolean {
    return screenSize !== 'mobile'; // Oculta alças de arrasto no móvel
  }
}
